/**
 * Brand Consultant Session Management Hook
 * 
 * Manages session state and persistence for Brand Consultant conversations.
 * Handles session creation, loading, and message history.
 */

import { useState, useCallback } from 'react';
import { useAuth } from '@/lib/auth.tsx';
import { ConsultantSession, Message } from '../types';
import { toast } from '@/components/ui/use-toast';

export function useConsultantSession() {
  const [session, setSession] = useState<ConsultantSession | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const { session: authSession } = useAuth();

  const createSession = useCallback(async (
    targetBrandId: string,
    sessionName?: string,
    sessionType: ConsultantSession['session_type'] = 'general'
  ): Promise<ConsultantSession | null> => {
    if (!authSession) {
      setError(new Error('Authentication required'));
      return null;
    }

    setLoading(true);
    setError(null);

    try {
      const workerUrl = import.meta.env.VITE_WORKERS_URL;
      const response = await fetch(`${workerUrl}/rainfactory/brand-consultant/sessions`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authSession.access_token}`,
          'Content-Type': 'application/json',
          'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY || '',
        },
        body: JSON.stringify({
          brandId: targetBrandId,
          sessionName: sessionName || `Session ${new Date().toLocaleDateString()}`,
          sessionType
        })
      });

      if (!response.ok) {
        throw new Error('Failed to create session');
      }

      const data = await response.json();
      const newSession = data.session as ConsultantSession;
      setSession(newSession);

      // Store session ID in localStorage for persistence
      localStorage.setItem(`brand-consultant-session-${targetBrandId}`, newSession.id);

      return newSession;
    } catch (err) {
      console.error('Error creating consultant session:', err);
      setError(err as Error);
      toast({
        title: 'Error',
        description: 'Failed to create consultant session. Please try again.',
        variant: 'destructive'
      });
      return null;
    } finally {
      setLoading(false);
    }
  }, [authSession]);

  const loadSession = useCallback(async (targetBrandId: string): Promise<ConsultantSession | null> => {
    if (!authSession) {
      setError(new Error('Authentication required'));
      return null;
    }

    setLoading(true);
    setError(null);

    try {
      // Try to get stored session ID first
      const storedSessionId = localStorage.getItem(`brand-consultant-session-${targetBrandId}`);

      if (storedSessionId) {
        // Try to load the stored session
        const workerUrl = import.meta.env.VITE_WORKERS_URL;
        const response = await fetch(`${workerUrl}/rainfactory/brand-consultant/sessions/${targetBrandId}`, {
          headers: {
            'Authorization': `Bearer ${authSession.access_token}`,
            'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY || '',
          }
        });

        if (response.ok) {
          const data = await response.json();
          const sessions = data.sessions as ConsultantSession[];
          const activeSession = sessions.find(s => s.id === storedSessionId && s.status === 'active');

          if (activeSession) {
            setSession(activeSession);
            return activeSession;
          }
        }
      }

      // If no stored session or it's not valid, create a new one
      return await createSession(targetBrandId);

    } catch (err) {
      console.error('Error loading consultant session:', err);
      setError(err as Error);
      // Try to create a new session as fallback
      return await createSession(targetBrandId);
    } finally {
      setLoading(false);
    }
  }, [authSession, createSession]);

  const loadMessages = useCallback(async (sessionId: string): Promise<Message[]> => {
    if (!authSession) {
      return [];
    }

    try {
      const workerUrl = import.meta.env.VITE_WORKERS_URL;
      const response = await fetch(`${workerUrl}/rainfactory/brand-consultant/messages/${sessionId}`, {
        headers: {
          'Authorization': `Bearer ${authSession.access_token}`,
          'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY || '',
        }
      });

      if (!response.ok) {
        throw new Error('Failed to load messages');
      }

      const data = await response.json();
      return data.messages || [];
    } catch (err) {
      console.error('Error loading messages:', err);
      return [];
    }
  }, [authSession]);

  const getAllSessions = useCallback(async (targetBrandId: string): Promise<ConsultantSession[]> => {
    if (!authSession) {
      return [];
    }

    try {
      const workerUrl = import.meta.env.VITE_WORKERS_URL;
      const response = await fetch(`${workerUrl}/rainfactory/brand-consultant/sessions/${targetBrandId}`, {
        headers: {
          'Authorization': `Bearer ${authSession.access_token}`,
          'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY || '',
        }
      });

      if (response.ok) {
        const data = await response.json();
        return data.sessions || [];
      }
      return [];
    } catch (err) {
      console.error('Error loading sessions:', err);
      return [];
    }
  }, [authSession]);

  const switchToSession = useCallback(async (sessionId: string, targetBrandId: string): Promise<ConsultantSession | null> => {
    const sessions = await getAllSessions(targetBrandId);
    const targetSession = sessions.find(s => s.id === sessionId);

    if (targetSession) {
      setSession(targetSession);
      localStorage.setItem(`brand-consultant-session-${targetBrandId}`, sessionId);
      return targetSession;
    }

    return null;
  }, [getAllSessions]);

  const clearSession = useCallback((targetBrandId: string) => {
    setSession(null);
    localStorage.removeItem(`brand-consultant-session-${targetBrandId}`);
  }, []);

  return {
    session,
    loading,
    error,
    createSession,
    loadSession,
    loadMessages,
    getAllSessions,
    switchToSession,
    clearSession
  };
}